// WelcomePage.jsx - Fixed to preserve QR session

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Styles/WelcomePage.css';

const WelcomePage = () => {
  const navigate = useNavigate();
  const [resturantName, setResturantName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isFetchingName = useRef(false);
  const isWelcoming = useRef(false);

  useEffect(() => {
    // Check if we have a valid QR session before clearing
    const hasValidQR = sessionStorage.getItem('hasValidQR');
    
    if (!hasValidQR) {
      // Only clear session if not coming from QR code
      sessionStorage.clear();
    } else {
      // Preserve QR session data
      const qrCode = sessionStorage.getItem('qrCode');
      const tableInfo = sessionStorage.getItem('tableInfo');
      const audioEnabled = sessionStorage.getItem('audioEnabled');
      const userHasInteracted = sessionStorage.getItem('userHasInteracted');
      
      // Clear everything except QR-related data
      const preserveKeys = ['qrCode', 'tableInfo', 'hasValidQR', 'audioEnabled', 'userHasInteracted'];
      const keysToRemove = [];
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!preserveKeys.includes(key)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    }

    const fetchData = async () => {
      if (isFetchingName.current) return;
      isFetchingName.current = true;
      
      try {
        const restaurantId = sessionStorage.getItem('restaurantId');
        const nameUrl = restaurantId
          ? `https://waiter-ai.onrender.com/api/resturant/name/${restaurantId}`
          : 'https://waiter-ai.onrender.com/api/resturant/name';
        const response = await fetch(nameUrl);

        if (response.status !== 200) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();
        setResturantName(result);
        return result;

      } catch (err) {
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError(true);
        }
        throw err;
      } finally {
        isFetchingName.current = false;
        setLoading(false);
      }
    };

    const welcoming = async (name) => {
      if (isWelcoming.current) return;
      isWelcoming.current = true;

      try {
        const response = await fetch('https://waiter-ai.onrender.com/api/tts/welcome', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resturantName: name
          }),
        });

        await new Promise(resolve => setTimeout(resolve, 2500)); 

      } catch (error) {
        console.error('Error during POST:', error);
      } finally {
        isWelcoming.current = false;
        localStorage.setItem('resturantName', name);
        navigate('/order');
      }
    };

    // Main execution
    const executeSequence = async () => {
      try {
        const fetchedName = await fetchData();
        
        if (fetchedName && !isWelcoming.current) {
          await welcoming(fetchedName);
        }
      } catch (error) {
        console.error('Error in sequence:', error);
      }
    };

    executeSequence();
  }, [navigate]);

  return (
    loading ? (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <h2>Loading...</h2>
        </div>
      </div>
    ) : error ? (
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h2>Experiencing internal error. Please try again at a later time</h2>
        </div>
      </div>
    ) : (
      <div className="welcome-container">
        <div className="welcome-content">
          <h1 className="welcome-title">Welcome to {resturantName}</h1>
        </div>
      </div>
    )
  );
};

export default WelcomePage;